import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { usePromoContext } from '@/context/PromoContext';
import {
  cartCreate,
  cartDiscountCodesUpdate,
  cartGet,
  cartLinesAdd,
  cartLinesRemove,
  cartLinesUpdate,
} from '@/services/shopify/shopifyQueries';
import type { ShopifyCart } from '@/types/shopify';
import {
  enqueueCartMutation,
  withCartConflictRetry,
} from '@/utils/cartMutation';

interface CartContextValue {
  cartId: string | null;
  cart: ShopifyCart | null;
  loading: boolean;
  addItem: (variantId: string, quantity: number) => Promise<void>;
  updateItem: (lineId: string, quantity: number) => Promise<void>;
  removeItem: (lineId: string) => Promise<void>;
  checkout: () => void;
}

const CartContext = createContext<CartContextValue | undefined>(undefined);

const STORAGE_KEY = 'shopify_cart_id';

function getDesiredDiscountCodes(
  promo: { code?: string } | null | undefined,
): string[] {
  return promo?.code ? [promo.code] : [];
}

function isPromoAlreadySynced(cart: ShopifyCart, desiredCodes: string[]): boolean {
  const currentCodes = cart.discountCodes?.map((discount) => discount.code) ?? [];

  return (
    desiredCodes.length === currentCodes.length &&
    desiredCodes.every((code, index) => code === currentCodes[index])
  );
}

export const CartProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { promo } = usePromoContext();
  const [cartId, setCartId] = useState<string | null>(null);
  const [cart, setCart] = useState<ShopifyCart | null>(null);
  const [loading, setLoading] = useState(false);
  const desiredDiscountCodes = useMemo(() => getDesiredDiscountCodes(promo), [promo]);

  const syncPromoDiscountCodes = useCallback(
    async (currentCartId: string, currentCart?: ShopifyCart | null) => {
      const cartSnapshot = currentCart ?? (await cartGet(currentCartId));
      if (!cartSnapshot) return null;
      if (isPromoAlreadySynced(cartSnapshot, desiredDiscountCodes)) return cartSnapshot;

      return withCartConflictRetry(() =>
        cartDiscountCodesUpdate(currentCartId, desiredDiscountCodes),
      );
    },
    [desiredDiscountCodes],
  );

  const applyPromoToCart = useCallback(
    async (currentCartId: string, currentCart: ShopifyCart) => {
      try {
        return (await syncPromoDiscountCodes(currentCartId, currentCart)) ?? currentCart;
      } catch {
        return currentCart;
      }
    },
    [syncPromoDiscountCodes],
  );

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const storedId = window.localStorage.getItem(STORAGE_KEY);
    if (!storedId) return;

    setCartId(storedId);
    setLoading(true);

    void enqueueCartMutation(async () => {
      const existingCart = await cartGet(storedId);
      if (!existingCart) {
        setCartId(null);
        setCart(null);
        window.localStorage.removeItem(STORAGE_KEY);
        return;
      }

      const promoSyncedCart = await applyPromoToCart(existingCart.id, existingCart);
      setCartId(existingCart.id);
      setCart(promoSyncedCart);
    }).finally(() => setLoading(false));
  }, [applyPromoToCart]);

  const ensureCart = useCallback(async () => {
    if (cartId && cart) return { cartId, cart };

    if (cartId && !cart) {
      setLoading(true);
      const existingCart = await enqueueCartMutation(() => cartGet(cartId));
      setLoading(false);

      if (existingCart) {
        const promoSyncedCart = await applyPromoToCart(existingCart.id, existingCart);
        setCart(promoSyncedCart);
        return { cartId: existingCart.id, cart: promoSyncedCart };
      }

      if (typeof window !== 'undefined') {
        window.localStorage.removeItem(STORAGE_KEY);
      }
      setCartId(null);
      setCart(null);
    }

    setLoading(true);
    const newCart = await enqueueCartMutation(() => cartCreate());
    setLoading(false);

    if (!newCart) {
      throw new Error('Unable to create Shopify cart');
    }

    const normalizedCart = await applyPromoToCart(newCart.id, newCart);

    setCartId(normalizedCart.id);
    setCart(normalizedCart);

    if (typeof window !== 'undefined') {
      window.localStorage.setItem(STORAGE_KEY, normalizedCart.id);
    }

    return { cartId: normalizedCart.id, cart: normalizedCart };
  }, [cartId, cart, applyPromoToCart]);

  const addItem = useCallback(
    async (variantId: string, quantity: number) => {
      const { cartId: currentCartId } = await ensureCart();
      setLoading(true);

      try {
        await enqueueCartMutation(async () => {
          const lineUpdatedCart = await withCartConflictRetry(() =>
            cartLinesAdd(currentCartId, [{ merchandiseId: variantId, quantity }]),
          );

          if (!lineUpdatedCart) return;

          const promoUpdatedCart = await applyPromoToCart(
            currentCartId,
            lineUpdatedCart,
          );
          setCart(promoUpdatedCart);
        });
      } finally {
        setLoading(false);
      }
    },
    [ensureCart, applyPromoToCart],
  );

  const updateItem = useCallback(
    async (lineId: string, quantity: number) => {
      if (!cartId || !cart) return;

      const line = cart.lines.edges.find(
        (edge) => edge.node.id === lineId,
      )?.node;
      if (!line) return;

      setLoading(true);

      try {
        await enqueueCartMutation(async () => {
          const updatedCart = await withCartConflictRetry(() =>
            cartLinesUpdate(cartId, [
              {
                id: lineId,
                merchandiseId: line.merchandise.id,
                quantity,
              },
            ]),
          );

          if (!updatedCart) return;

          const promoUpdatedCart = await applyPromoToCart(cartId, updatedCart);
          setCart(promoUpdatedCart);
        });
      } finally {
        setLoading(false);
      }
    },
    [cartId, cart, applyPromoToCart],
  );

  const removeItem = useCallback(
    async (lineId: string) => {
      if (!cartId) return;

      setLoading(true);

      try {
        await enqueueCartMutation(async () => {
          const updatedCart = await withCartConflictRetry(() =>
            cartLinesRemove(cartId, [lineId]),
          );

          if (!updatedCart) return;

          const promoUpdatedCart = await applyPromoToCart(cartId, updatedCart);
          setCart(promoUpdatedCart);
        });
      } finally {
        setLoading(false);
      }
    },
    [cartId, applyPromoToCart],
  );

  const checkout = useCallback(() => {
    if (!cart?.checkoutUrl) return;
    if (typeof window === 'undefined') return;

    try {
      const checkoutUrl = new URL(cart.checkoutUrl);
      checkoutUrl.searchParams.set('return_to', window.location.href);
      window.location.href = checkoutUrl.toString();
    } catch {
      window.location.href = cart.checkoutUrl;
    }
  }, [cart]);

  const value: CartContextValue = {
    cartId,
    cart,
    loading,
    addItem,
    updateItem,
    removeItem,
    checkout,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

export const useCartContext = (): CartContextValue => {
  const ctx = useContext(CartContext);
  if (!ctx) {
    throw new Error('useCartContext must be used within CartProvider');
  }
  return ctx;
};
